import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { BsCameraVideo, BsPlayCircleFill } from 'react-icons/bs';
import { FaYoutube, FaEye, FaHeart } from 'react-icons/fa';
import { FiSearch, FiChevronLeft, FiChevronRight, FiPlus } from 'react-icons/fi';
import WebsiteLayout from '../../../layout/website/WebsiteLayout';
import bannerGif from '../../../../assets/images/banner.gif';
import logo from '../../../../assets/images/logo.png';
import { getVideos } from '../../../../store/videos/videosSlice';
import {
  PageWrapper, PageBanner, ControlsBar, FilterBtn,
  VideosGrid, VideoCard, PaginationBar, PageBtn,
} from './VideosStyle';
import { backendUrl } from "../../../../utilities/axios";
import { SkeletonCard, NoData } from '../../../shared/WebsiteFeedback';

const PER_PAGE = 8;

const CATEGORY_KEYS = ['all', 'wars', 'farming', 'discussions', 'reviews', 'tutorials'];
const CATEGORY_DB_MAP = {
  wars: 'حروب',
  farming: 'زراعة',
  discussions: 'نقاشات',
  reviews: 'مراجعات',
  tutorials: 'تعليم',
};

const SORT_KEYS = ['newest', 'views', 'likes'];

const toNum = s => {
  if (!s) return 0;
  const n = parseFloat(s);
  if (String(s).includes('K')) return n * 1000;
  if (String(s).includes('M')) return n * 1000000;
  return n;
};

const Videos = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data: videosData, isLoading } = useSelector(state => state.videos);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(getVideos({ page: 1, size: 100, query: '', filter: JSON.stringify({ is_active: true }) }));
  }, [dispatch]);

  const allVideos = videosData?.data || [];

  const getCategoryLabel = (key) => {
    const map = {
      all: t('website.videos.cat_all'),
      wars: t('website.videos.cat_wars'),
      farming: t('website.videos.cat_farming'),
      discussions: t('website.videos.cat_discussions'),
      reviews: t('website.videos.cat_reviews'),
      tutorials: t('website.videos.cat_tutorials'),
    };
    return map[key] || key;
  };

  const getSortLabel = (key) => {
    const map = {
      newest: t('website.videos.sort_newest'),
      views: t('website.videos.sort_most_viewed'),
      likes: t('website.videos.sort_most_liked'),
    };
    return map[key] || key;
  };

  const processed = useMemo(() => {
    let list = allVideos;
    if (category !== 'all') {
      const dbVal = CATEGORY_DB_MAP[category];
      list = list.filter(v => v.category === dbVal);
    }
    if (search.trim()) list = list.filter(v => v.title.includes(search.trim()) || v.creator.includes(search.trim()));
    return [...list].sort((a, b) => {
      if (sort === 'views') return toNum(b.views) - toNum(a.views);
      if (sort === 'likes') return toNum(b.likes) - toNum(a.likes);
      return new Date(b.created_at) - new Date(a.created_at);
    });
  }, [search, category, sort, allVideos]);

  const totalPages = Math.max(1, Math.ceil(processed.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageVideos = processed.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const goTo = p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const getThumb = (video) => video.thumbnail ? `${backendUrl}/videos/${video.thumbnail}` : bannerGif;
  const getCreatorAvatar = (video) => video.creator_avatar ? `${backendUrl}/videos/${video.creator_avatar}` : logo;

  return (
    <WebsiteLayout>
      <PageWrapper>
        <PageBanner>
          <div>
            <div className="page-title"><BsCameraVideo /> {t('website.videos.title')}</div>
            <div className="page-sub">{t('website.videos.subtitle')}</div>
          </div>
          <Link to="/add-video" className="add-video-btn">
            <FiPlus /> {t('website.videos.add_video_btn', 'أضف فيديو')}
          </Link>
        </PageBanner>

        <ControlsBar>
          <div className="search-wrap">
            <FiSearch />
            <input placeholder={t('website.videos.search_placeholder')} value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <div className="filter-pills">
            {CATEGORY_KEYS.map(key => (
              <FilterBtn key={key} active={category === key} onClick={() => { setCategory(key); setPage(1); }}>
                {getCategoryLabel(key)}
              </FilterBtn>
            ))}
          </div>
          <select className="sort-select" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
            {SORT_KEYS.map(key => <option key={key} value={key}>{getSortLabel(key)}</option>)}
          </select>
        </ControlsBar>

        {isLoading ? (
          <VideosGrid>
            {Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)}
          </VideosGrid>
        ) : pageVideos.length === 0 ? (
          <NoData />
        ) : (
          <VideosGrid>
          {pageVideos.map(video => (
            <VideoCard key={video._id}>
              <div className="thumb-wrap">
                <img src={getThumb(video)} alt={video.title} />
                {video.youtube_url ? (
                  <a href={video.youtube_url} target="_blank" rel="noreferrer" className="play-btn">
                    <BsPlayCircleFill />
                  </a>
                ) : (
                  <div className="play-btn"><BsPlayCircleFill /></div>
                )}
                {video.duration && <span className="duration-badge">{video.duration}</span>}
              </div>
              <div className="video-body">
                <div className="video-title">{video.title}</div>
                <div className="creator-row">
                  <img src={getCreatorAvatar(video)} alt={video.creator} className="creator-avatar" />
                  <span className="creator-name">{video.creator}</span>
                  <FaYoutube className="yt-icon" />
                </div>
                <div className="video-stats">
                  <span className="stat"><FaEye /> {video.views}</span>
                  <span className="stat"><FaHeart /> {video.likes}</span>
                </div>
              </div>
            </VideoCard>
          ))}
          </VideosGrid>
        )}

        {!isLoading && totalPages > 1 && (
          <PaginationBar>
            <div className="page-info">
              {t('website.videos.showing')} <span>{(currentPage - 1) * PER_PAGE + 1}–{Math.min(currentPage * PER_PAGE, processed.length)}</span> {t('website.videos.of')} <span>{processed.length}</span> {t('website.videos.video_unit')}
            </div>
            <div className="pages">
              <PageBtn disabled={currentPage === 1} onClick={() => goTo(currentPage - 1)}><FiChevronRight /></PageBtn>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <PageBtn key={n} active={n === currentPage} onClick={() => goTo(n)}>{n}</PageBtn>
              ))}
              <PageBtn disabled={currentPage === totalPages} onClick={() => goTo(currentPage + 1)}><FiChevronLeft /></PageBtn>
            </div>
          </PaginationBar>
        )}
      </PageWrapper>
    </WebsiteLayout>
  );
};

export default Videos;
